import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Loader2 } from "lucide-react";
import { formatCstDateTimeLocal } from "../utils/cstDate";
import { toast } from "react-hot-toast";

export const PromosEditModal = ({
  isOpen,
  onClose,
  transaction,
  onUpdateTransaction,
  currentUser,
  transactionDetails = [],
}) => {
  const [formData, setFormData] = useState({
    date: "",
    playerName: "",
    notes: "",
  });
  const [selectedPromo, setSelectedPromo] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const isWithdraw = transaction?.transaction_type === "withdraw";
  const promoOptions = Array.isArray(transactionDetails) ? transactionDetails : [];

  useEffect(() => {
    if (transaction) {
      const notes = transaction.notes || "";
      setFormData({
        date: formatCstDateTimeLocal(transaction.date),
        playerName: transaction.player_name || "",
        notes,
      });
      setSelectedPromo(
        Array.isArray(transactionDetails) && transactionDetails.includes(notes)
          ? notes
          : ""
      );
    }
  }, [transaction, transactionDetails]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePromotionSelect = (value) => {
    if (!value || value === "select") {
      setSelectedPromo("");
      return;
    }
    setSelectedPromo(value);
    setFormData((prev) => ({ ...prev, notes: value }));
  };

  const handleSubmit = async () => {
    if (!transaction) return;

    if (!formData.date) {
      toast.error("Please select a date");
      return;
    }

    if (isWithdraw && !formData.playerName.trim()) {
      toast.error("Please enter a player name");
      return;
    }

    if (isWithdraw && !formData.notes.trim()) {
      toast.error("Please select a promotion");
      return;
    }

    setSubmitting(true);
    const isDateUnchanged =
      formData.date === formatCstDateTimeLocal(transaction.date);

    try {
      const success = await onUpdateTransaction(transaction.id, {
        date: isDateUnchanged ? transaction.date : formData.date,
        player_name: isWithdraw ? formData.playerName.trim() : "",
        playerName: isWithdraw ? formData.playerName.trim() : "",
        notes: isWithdraw ? formData.notes.trim() : "",
      });

      if (success) {
        onClose();
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Failed to update transaction");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !transaction) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Edit Record</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date *
            </label>
            <Input
              type="datetime-local"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="w-full"
              required
            />
          </div>

          {isWithdraw && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Player Name *
                </label>
                <Input
                  name="playerName"
                  type="text"
                  value={formData.playerName}
                  onChange={handleChange}
                  placeholder="Enter player name"
                  className="w-full h-10"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Select Promotion *
                </label>
                <Select value={selectedPromo || undefined} onValueChange={handlePromotionSelect}>
                  <SelectTrigger className="w-full h-10">
                    <SelectValue placeholder="Choose a promotion" />
                  </SelectTrigger>
                  <SelectContent>
                    {promoOptions.length === 0 ? (
                      <SelectItem value="select" disabled>
                        No promotions configured
                      </SelectItem>
                    ) : (
                      promoOptions.map((promotion) => (
                        <SelectItem key={promotion} value={promotion}>
                          {promotion}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Promotion Details
                </label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  placeholder="Selected promotion appears here (you can adjust if needed)"
                  className="w-full min-h-[80px] p-3 border border-gray-300 rounded-md resize-vertical focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-transparent"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Updated By
            </label>
            <Input
              type="text"
              value={currentUser?.username || currentUser?.name || "Current User"}
              disabled
              className="w-full bg-gray-100"
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={
                submitting ||
                (isWithdraw && !formData.playerName.trim()) ||
                (isWithdraw && !formData.notes.trim())
              }
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                "Update"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
